import type { FC, FocusEvent, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { Marquee } from '@/components/generals/marquee'
import { NewsBar } from '@/components/generals/news-bar'
import { PillLink } from '@/components/generals/pill-link'
import { Wordmark } from '@/components/generals/wordmark'

import {
    clientAreaLabel,
    company,
    mainPhone,
    menuNav,
    primaryNav,
    siteConfig
} from '@/constants/site'

type HeaderTheme = 'dark' | 'light'

const SCROLL_THRESHOLD = 80
const THEME_PROBE = 40
const MENU_ID = 'site-menu'

const MenuDots: FC<{ open: boolean }> = ({ open }): ReactNode => {
    return (
        <span
            aria-hidden='true'
            className={cn('grid grid-cols-3 gap-[3px] transition-transform duration-300', open && 'rotate-90')}
        >
            {Array.from({ length: 6 }).map((_, index) => (
                <span key={index} className='size-[3px] rounded-full bg-current' />
            ))}
        </span>
    )
}

const LinkedInPill: FC<{ dark: boolean }> = ({ dark }): ReactNode => {
    return (
        <a
            href={company.socials.linkedin}
            target='_blank'
            rel='noopener noreferrer'
            aria-label={`Siga a ${siteConfig.name} no LinkedIn`}
            className={cn(
                'hidden h-10 w-44 items-center overflow-hidden rounded-full border transition-colors xl:flex',
                dark ? 'border-white/25 hover:border-white' : 'border-ink/20 hover:border-ink'
            )}
        >
            <Marquee duration={12} className='w-full [mask-image:linear-gradient(to_right,transparent,black_15%,black_85%,transparent)]'>
                {[0, 1].map((key) => (
                    <span key={key} className='flex items-center gap-3 pr-3 font-mono text-[11px] uppercase tracking-[0.14em]'>
                        Siga a Ativa no LinkedIn
                        <span className='size-1 rounded-full bg-current' />
                    </span>
                ))}
            </Marquee>
        </a>
    )
}

export const Header: FC = (): ReactNode => {
    const router = useRouter()
    const [theme, setTheme] = useState<HeaderTheme>('dark')
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)
    const panelRef = useRef<HTMLDivElement>(null)
    const toggleRef = useRef<HTMLButtonElement>(null)

    // Follow the scroll position and the colour of the section sitting under the header.
    useEffect(() => {
        let frame = 0

        const update = (): void => {
            frame = 0
            setScrolled(window.scrollY > SCROLL_THRESHOLD)

            let next: HeaderTheme = 'dark'
            const sections = Array.from(document.querySelectorAll<HTMLElement>('[data-header-theme]'))

            for (const section of sections) {
                const rect = section.getBoundingClientRect()

                if (rect.top <= THEME_PROBE && rect.bottom > THEME_PROBE) {
                    next = section.dataset.headerTheme === 'light' ? 'light' : 'dark'
                }
            }

            setTheme(next)
        }

        const schedule = (): void => {
            if (!frame) frame = window.requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', schedule, { passive: true })
        window.addEventListener('resize', schedule)

        return () => {
            window.removeEventListener('scroll', schedule)
            window.removeEventListener('resize', schedule)
            if (frame) window.cancelAnimationFrame(frame)
        }
    }, [router.asPath])

    useEffect(() => {
        const close = (): void => setOpen(false)

        router.events.on('routeChangeStart', close)
        router.events.on('hashChangeStart', close)

        return () => {
            router.events.off('routeChangeStart', close)
            router.events.off('hashChangeStart', close)
        }
    }, [router.events])

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key !== 'Escape') return

            setOpen(false)
            toggleRef.current?.focus()
        }

        const onPointerDown = (event: PointerEvent): void => {
            const target = event.target as Node

            if (panelRef.current?.contains(target) || toggleRef.current?.contains(target)) return

            setOpen(false)
        }

        const previousOverflow = document.body.style.overflow

        document.body.style.overflow = 'hidden'
        document.addEventListener('keydown', onKeyDown)
        document.addEventListener('pointerdown', onPointerDown)
        panelRef.current?.querySelector<HTMLElement>('a')?.focus()

        return () => {
            document.body.style.overflow = previousOverflow
            document.removeEventListener('keydown', onKeyDown)
            document.removeEventListener('pointerdown', onPointerDown)
        }
    }, [open])

    const onPanelBlur = (event: FocusEvent<HTMLDivElement>): void => {
        const next = event.relatedTarget as Node | null

        if (!next) return
        if (event.currentTarget.contains(next) || toggleRef.current?.contains(next)) return

        setOpen(false)
    }

    const isDark = theme === 'dark'
    const solid = scrolled || open

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-50 transition-[background-color,color,box-shadow] duration-300',
                isDark ? 'text-white' : 'text-ink',
                solid && (isDark ? 'bg-ink/75 backdrop-blur-md' : 'bg-white/85 shadow-[0_1px_0_rgba(11,11,13,0.06)] backdrop-blur-md')
            )}
        >
            <div
                className={cn(
                    'grid transition-[grid-template-rows,opacity] duration-300',
                    scrolled ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'
                )}
            >
                <div className='overflow-hidden' inert={scrolled}>
                    <NewsBar />
                </div>
            </div>

            <Container className='relative flex h-16 items-center justify-between gap-6 lg:h-[4.5rem]'>
                <Link href='/' aria-label={`${siteConfig.name}, página inicial`} className='relative z-10 -m-2 p-2'>
                    <Wordmark compact={scrolled} />
                </Link>

                <nav
                    aria-label='Principal'
                    inert={scrolled || open}
                    className={cn(
                        'absolute left-[30%] top-4 hidden flex-col gap-1.5 transition-[opacity,translate] duration-300 lg:flex',
                        scrolled || open ? 'pointer-events-none -translate-y-2 opacity-0' : 'opacity-100'
                    )}
                >
                    {primaryNav.map((item) => (
                        <Link
                            key={item.href}
                            href={item.href}
                            className='group flex items-center font-mono text-xs uppercase tracking-[0.14em] opacity-85 transition-opacity hover:opacity-100'
                        >
                            <span aria-hidden='true' className='mr-0 h-px w-0 bg-current transition-all duration-300 group-hover:mr-2 group-hover:w-3' />
                            {item.label}
                        </Link>
                    ))}
                </nav>

                <div className='relative z-10 flex items-center gap-3'>
                    <LinkedInPill dark={isDark} />

                    <PillLink
                        href={company.clientAreaUrl}
                        variant={isDark ? 'light' : 'dark'}
                        size='sm'
                        arrow={false}
                        className='hidden sm:inline-flex'
                    >
                        {clientAreaLabel}
                    </PillLink>

                    <button
                        ref={toggleRef}
                        type='button'
                        aria-expanded={open}
                        aria-controls={MENU_ID}
                        onClick={() => setOpen((current) => !current)}
                        className={cn(
                            'flex h-10 items-center gap-3 rounded-full border px-4 font-mono text-[11px] uppercase tracking-[0.14em] transition-colors',
                            isDark ? 'border-white/25 hover:border-white' : 'border-ink/20 hover:border-ink',
                            scrolled || open ? 'lg:flex' : 'lg:hidden'
                        )}
                    >
                        <MenuDots open={open} />
                        <span>{open ? 'Fechar' : 'Menu'}</span>
                    </button>
                </div>

                {open && (
                    <div
                        ref={panelRef}
                        id={MENU_ID}
                        onBlur={onPanelBlur}
                        className='absolute right-5 top-full mt-2 max-h-[calc(100svh-6rem)] w-[calc(100vw-2.5rem)] max-w-sm origin-top-right overflow-y-auto rounded-2xl bg-ink text-white shadow-2xl ring-1 ring-white/10 animate-in fade-in zoom-in-95 duration-200 md:right-8 lg:right-10'
                    >
                        <nav aria-label='Menu do site' className='p-2'>
                            <ul>
                                {menuNav.map((item, index) => (
                                    <li key={item.href}>
                                        <Link
                                            href={item.href}
                                            className='group flex items-center justify-between rounded-xl px-4 py-3 transition-colors hover:bg-white/[0.06] focus-visible:bg-white/[0.06] focus-visible:outline-none'
                                        >
                                            <span className='flex items-baseline gap-4'>
                                                <span className='font-mono text-[10px] text-white/40'>
                                                    {String(index + 1).padStart(2, '0')}
                                                </span>

                                                <span className='font-heading text-base uppercase tracking-[-0.01em] sm:text-lg'>
                                                    {item.label}
                                                </span>
                                            </span>

                                            <ArrowUpRight
                                                aria-hidden='true'
                                                className='size-4 text-white/40 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-white'
                                            />
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        <div className='border-t border-white/10 px-6 py-5'>
                            <p className='font-mono text-[10px] uppercase tracking-[0.16em] text-white/50'>
                                Fale conosco
                            </p>

                            <a href={mainPhone.href} className='mt-3 block text-sm transition-colors hover:text-brand-soft'>
                                {mainPhone.city} {mainPhone.label}
                            </a>

                            <a
                                href={company.whatsappUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='mt-1 block text-sm transition-colors hover:text-brand-soft'
                            >
                                WhatsApp
                            </a>

                            <div className='mt-5 flex flex-wrap gap-x-5 gap-y-2 font-mono text-[10px] uppercase tracking-[0.14em] text-white/60'>
                                <a
                                    href={company.simulationUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='transition-colors hover:text-white'
                                >
                                    Faça uma simulação
                                </a>

                                <a
                                    href={company.socials.instagram}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='transition-colors hover:text-white'
                                >
                                    Instagram
                                </a>
                            </div>
                        </div>

                        <div className='p-2 pt-0 sm:hidden'>
                            <PillLink href={company.clientAreaUrl} variant='light' className='w-full'>
                                {clientAreaLabel}
                            </PillLink>
                        </div>
                    </div>
                )}
            </Container>
        </header>
    )
}
